import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react'

import {
  Button, Check, classnames, Collapse3, Copy, Expand3, Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string } from 'prop-types'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import useIsClamped from './useIsClamped'

const controlIconSize = { height: '16px', width: '16px' }
const expandIcon = <Icon svgComponent={Expand3} size={controlIconSize} color="darker" notSpaced />
const collapseIcon = <Icon svgComponent={Collapse3} size={controlIconSize} color="darker" notSpaced />
const copyIcon = <Icon svgComponent={Copy} size={controlIconSize} color="darker" notSpaced />
const copiedIcon = <Icon svgComponent={Check} size={controlIconSize} color="darker" notSpaced />

export const COPIED_FEEDBACK_MS = 1000

// The prompt is clamped to eight lines until expanded. A successful copy turns the copy icon into
// a check for a moment and announces it; a failed one leaves the control as it was.
function Prompt({ text = '' }) {
  const t = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const resetTimerRef = useRef(null)
  const [textRef, isClamped] = useIsClamped(text, isExpanded)

  useEffect(() => () => clearTimeout(resetTimerRef.current), [])

  const handleToggle = useCallback(() => setIsExpanded(expanded => !expanded), [])

  // The Clipboard API is absent outside a secure context, so the control does nothing there
  // rather than throwing on click. A repeat click restarts the confirmation.
  const handleCopy = useCallback(() => {
    if (!navigator.clipboard?.writeText) return

    navigator.clipboard.writeText(text)
      .then(() => {
        setIsCopied(true)
        clearTimeout(resetTimerRef.current)
        resetTimerRef.current = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS)
      })
      .catch(() => {})
  }, [text])

  if (!text) return null

  return (
    <div className="image-gallery-modal__prompt">
      <div className="image-gallery-modal__prompt-header">
        <span className="image-gallery-modal__prompt-label cgt-body-base-semibold">
          {t('imageGallery.modal.prompt')}
        </span>
        <div className="image-gallery-modal__prompt-controls">
          {(isClamped || isExpanded) && (
            <Button
              type="ghost"
              size="tiny"
              variant="only-icon"
              actionType="button"
              icon={isExpanded ? collapseIcon : expandIcon}
              customClass="image-gallery-modal__prompt-control"
              onClick={handleToggle}
              aria-expanded={isExpanded}
              aria-label={t(isExpanded ? 'imageGallery.modal.collapsePrompt' : 'imageGallery.modal.expandPrompt')}
            />
          )}
          <Button
            type="ghost"
            size="tiny"
            variant="only-icon"
            actionType="button"
            icon={isCopied ? copiedIcon : copyIcon}
            customClass="image-gallery-modal__prompt-control"
            onClick={handleCopy}
            aria-label={t(isCopied ? 'imageGallery.modal.copied' : 'imageGallery.modal.copyPrompt')}
          />
        </div>
        <span className="image-gallery-modal__prompt-status" role="status">
          {isCopied ? t('imageGallery.modal.promptCopied') : ''}
        </span>
      </div>
      <p
        ref={textRef}
        className={classnames(
          'image-gallery-modal__prompt-text cgt-body-base',
          !isExpanded && 'image-gallery-modal__prompt-text--clamped',
        )}
      >
        {text}
      </p>
    </div>
  )
}

Prompt.propTypes = {
  text: string,
}

export default memo(Prompt)
